import React, { useCallback, useEffect, useState } from "react";
import ApiClient from "../../../services/ApiClient";
import { PAID_TASK_STATUS_CREATED, PAID_TASK_STATUS_PAID } from "../paidTaskStatus";
import PaidTaskFilter from "./PaidTaskFilter";
import PaidTaskTable from "./PaidTaskTable";
import _ from "lodash";
import { paidTaskListFilters } from "../../../services/paidTaskService";
import RequestedPaidTasks from "./RequestedPaidTasks";
import PageHeadline from "../../PageHeadline/PageHeadline";
import { Box, Paper } from "@mui/material";
import ExternalPaidTaskTable from "./ExternalPaidTaskTable";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { PaidTask } from "../../../types/PaidTask";
import { BackofficeUser } from "../../../types/BackofficeUser";
import HumanResource from "../../../types/HumanResource";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { getProductData, productData } from "../../../services/Product/ProductService/productService";

const getApiFilterByDisplayType = (displayType: string, currentUser: BackofficeUser, humanResource?: HumanResource) => {
  switch (displayType) {
    case "assignableView":
      let filter = `?exists[deletedDate]=false&status=${PAID_TASK_STATUS_CREATED}`;
      humanResource?.trainedProducts.forEach((trainedProduct) => {
        //this should never reach the url character limit of 2000. and nowadays even urls longer than 2000 characters should work.
        // if this makes problems in the future tho one of these options will need to be implemented:
        // 1. custom filter on paid task that can handle productClassnames instead of lbProductClass
        // 2. frontend filtering
        // 3. fetching the product for each paidtask and using its productclassname

        if (trainedProduct !== "contractLaw") {
          filter = filter + "&backofficeCase.lbProductClass[]=" + getProductData(trainedProduct, "lbProductClass");
        } else {
          const contractLbProductClasses = Object.values(productData)
            .filter(
              (entry) =>
                entry.productCategory === "contractLaw" &&
                entry.lbProductClass !== getProductData("dataLeakContract", "lbProductClass")
            )
            .map((entry) => entry.lbProductClass);

          contractLbProductClasses.forEach((contractLbProductClass) => {
            filter = filter + "&backofficeCase.lbProductClass[]=" + contractLbProductClass;
          });
        }
      });

      return filter;
    case "assigneeView":
      return "?exists[deletedDate]=false&assignee.id=" + currentUser.id + "&status[lt]=" + PAID_TASK_STATUS_PAID;
    default:
    case "internalView":
      return "?exists[deletedDate]=false&status[lt]=" + PAID_TASK_STATUS_PAID;
  }
};

type PaidTaskContainerProps = {
  displayType: string;
  headline: string;
};
const PaidTaskContainer = ({ displayType, headline }: PaidTaskContainerProps) => {
  const currentUser = useCurrentUser();

  const humanResourceFilter = {
    backofficeUser: currentUser.id,
  };
  const { data: humanResources, isFetching: isFetchingHumanResources } = useQuery({
    queryKey: queryKeys.collection("human_resources", humanResourceFilter),
    queryFn: () => fetchCollection("human_resources", humanResourceFilter)
  });

  const [paidTasks, setPaidTasks] = useState<PaidTask[]>([]);
  const [paidTasksFiltered, setPaidTasksFiltered] = useState<PaidTask[]>([]);
  const [paidTaskFilter, setPaidTaskFilter] = useState(() => {
    switch (displayType) {
      case "assignableView":
        return "open";
      case "assigneeView":
        return "assigned";
      case "internalView":
      default:
        return "inReview";
    }
  });

  const refreshFunction = useCallback(() => {
    if (isFetchingHumanResources) {
      return;
    }
    ApiClient.get(
      "paid_tasks" + getApiFilterByDisplayType(displayType, currentUser, humanResources?.["hydra:member"]?.[0])
    ).then((paidTasks) => setPaidTasks(paidTasks["hydra:member"]));
  }, [displayType, currentUser, isFetchingHumanResources]);

  useEffect(() => {
    refreshFunction();
  }, [refreshFunction]);

  useEffect(() => {
    if (paidTasks === null) {
      return;
    }

    const currentFilter = _.get(paidTaskListFilters, displayType + "." + paidTaskFilter);

    if (!currentFilter) {
      setPaidTasksFiltered([]);
      return;
    }

    const filteredPaidTasks = _.filter(paidTasks, currentFilter.filter);
    const sortedPaidTasks = !!currentFilter.sorter
      ? _.sortBy(filteredPaidTasks, currentFilter.sorter)
      : filteredPaidTasks;
    setPaidTasksFiltered(sortedPaidTasks);
  }, [paidTasks, paidTaskFilter]);

  if (paidTasks === null || paidTasksFiltered === null) {
    return null;
  }

  return (
    <div>
      {_.includes(["assignableView", "assigneeView"], displayType) && <RequestedPaidTasks />}
      <Box sx={{ marginTop: "2.5rem" }}>
        <PageHeadline main={headline} />
      </Box>
      <PaidTaskFilter displayType={displayType} paidTaskFilter={paidTaskFilter} setPaidTaskFilter={setPaidTaskFilter} />
      <br />
      {displayType === "internalView" ? (
        <>
          <PaidTaskTable paidTasks={paidTasksFiltered} refreshFunction={refreshFunction} />
        </>
      ) : (
        <Paper>
          <ExternalPaidTaskTable paidTasks={paidTasksFiltered} />
        </Paper>
      )}
    </div>
  );
};
export default PaidTaskContainer;
